import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useStore, useQuery } from '../../../store/mst';
import { TagSearchResultModelType } from '../../../store/mst/models/TagSearchResultModel';
import { TagSearchResultModelSelector } from '../../../store/mst/models/TagSearchResultModel.base';
import { TagModelSelector } from '../../../store/mst/models/TagModel.base';

const SEARCH_DEBOUNCE_MS = 300;
const BROWSE_LIMIT = 100;
const SEARCH_LIMIT = 20;

interface SearchTagsData {
  searchTags: TagSearchResultModelType;
}

interface UseTagSearchResult {
  results: string[];
  loading: boolean;
  refetch: (search: string, options?: { forceNetwork?: boolean }) => void;
}

export function useTagSearch(searchTerm: string): UseTagSearchResult {
  const { model } = useStore();
  const { data: searchData, loading: queryLoading, setQuery } = useQuery<SearchTagsData>();

  const setQueryRef = useRef(setQuery);
  setQueryRef.current = setQuery;

  const runQuery = useMemo(
    () => (search: string, options?: { forceNetwork?: boolean }) => {
      const limit = search ? SEARCH_LIMIT : BROWSE_LIMIT;
      // Use 'cache-and-network' when forceNetwork (e.g. dropdown open) to get fresh data after
      // tags may have been created (e.g. via spreadsheet upload). Use 'cache-first' otherwise
      // to avoid unnecessary network requests.
      const fetchPolicy = search || options?.forceNetwork
        ? 'cache-and-network'
        : 'cache-first';
      setQueryRef.current(
        model.querySearchTags(
          { search: search || undefined, limit, offset: 0 },
          (qb: TagSearchResultModelSelector) => qb.tags((tag: TagModelSelector) => tag.uuid.name),
          { fetchPolicy },
        ),
      );
    },
    [model],
  );

  const debouncedSearch = useMemo(
    () => debounce(runQuery, SEARCH_DEBOUNCE_MS),
    [runQuery],
  );

  const hasSearchedRef = useRef(false);

  // Handle search term changes
  useEffect(() => {
    if (searchTerm) {
      hasSearchedRef.current = true;
      debouncedSearch(searchTerm);
    } else {
      debouncedSearch.cancel();
      // Only run browse query when user cleared the search, not on initial mount.
      // Initial empty search is handled by onDropdownOpen when the user opens the dropdown.
      if (hasSearchedRef.current) {
        runQuery('');
      }
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch, runQuery]);

  const currentResults = (searchData?.searchTags?.tags ?? [])
    .map((tag) => tag.name)
    .filter((name): name is string => Boolean(name));

  const cachedResultsRef = useRef<string[]>([]);
  const hasFetchedRef = useRef(false);
  if (!queryLoading) {
    hasFetchedRef.current = true;
    cachedResultsRef.current = currentResults;
  }

  // Show stale results while a new query is in-flight; only show
  // the loading indicator on the very first fetch (before any query has completed).
  const results = queryLoading ? cachedResultsRef.current : currentResults;
  return { results, loading: queryLoading && !hasFetchedRef.current, refetch: runQuery };
}
